/**
 * MainLayout — layout estándar del sitio público con Navbar + Footer.
 * Aplica a Home, terapias, tienda, galeria, sobre-mi, eventos, contacto, legal,
 * pago/resultado, 404.
 *
 * Envuelve todo en `AudioPlayerProvider` para que cualquier página pueda
 * inyectar/limpiar la URL de audio ambiental (ver EventoDetalle). El `<audio>`
 * real (invisible) y el FAB de mute/unmute también viven aquí — así navegar
 * entre rutas no reinicia el track ni crea audios fantasma.
 */
import { Outlet } from 'react-router-dom';
import { Navbar } from '../../components/layout/Navbar';
import { Footer } from '../../components/layout/Footer';
import { FloatingWhatsApp } from '../../components/layout/FloatingWhatsApp';
import { SkipLink } from '../../components/layout/SkipLink';
import { ScrollToTop } from '../../components/layout/ScrollToTop';
import { ScrollToTopButton } from '../../components/layout/ScrollToTopButton';
import { AudioPlayerProvider } from '../../features/audio/AudioPlayerContext';
import { GlobalAudioPlayer } from '../../features/audio/GlobalAudioPlayer';
import { AudioFloatingButton } from '../../features/audio/AudioFloatingButton';

export function MainLayout() {
  return (
    // El root NO lleva `overflow-x-hidden` — eso rompería el `position: sticky`
    // del Navbar (un padre con overflow != visible crea un nuevo scrolling
    // container y los hijos sticky se pegan dentro de él, no del viewport).
    // El clip horizontal vive en `<main>` que es hermano del Navbar.
    <AudioPlayerProvider>
      <div className="min-h-screen flex flex-col bg-cream-100">
        {/* Restore scroll al cambiar de ruta — debe vivir dentro del layout
            porque `useLocation` sólo funciona dentro del árbol de rutas. */}
        <ScrollToTop />
        <SkipLink />
        <Navbar />
        {/* tabIndex={-1} permite focus programático tras click en SkipLink.
            `w-full` explícito evita que el `<main>` herede un `min-content`
            incorrecto cuando algún hijo declara `min-w-[Xx%]`.
            `overflow-x-hidden` blinda contra scroll horizontal accidental en
            mobile (heros con ornamentos absolutos, carrusel de eventos con
            `min-w-[90%]`, grids cerca del breakpoint) sin afectar al Navbar
            sticky que vive fuera del `<main>`. */}
        <main
          id="main"
          tabIndex={-1}
          className="flex-1 w-full overflow-x-hidden focus:outline-none"
        >
          <Outlet />
        </main>
        <Footer />
        <FloatingWhatsApp />
        {/* Flecha "volver al inicio" — se posiciona encima del WhatsApp. */}
        <ScrollToTopButton />
        {/* Audio ambiental global — `<audio>` invisible + FAB de mute a la
            izquierda. Ambos consumen el `AudioPlayerContext`; el FAB sólo
            aparece cuando alguna página inyectó una URL. */}
        <GlobalAudioPlayer />
        <AudioFloatingButton />
      </div>
    </AudioPlayerProvider>
  );
}
